import type { Consent, IdFactory, StorageAdapter } from 'ProdantixSdk/core/types';

export interface IdentitySnapshot {
  anonymousId: string;
  distinctId: string;
  identified: boolean;
}

export interface IdentifyResult {
  anonymousToLink?: string;
  changed: boolean;
}

const MAX_ID_LENGTH = 200;

function valid(value: string | null): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const length = Array.from(value).length;
  return length >= 1 && length <= MAX_ID_LENGTH ? value : undefined;
}

function keyFor(namespace: string, suffix: string): string {
  return `pdx.${namespace}.${suffix}`;
}

export const CONSENT_SUFFIX = 'consent';

/** Reads the visitor's answer out of a store value. Anything but the shape the
 *  SDK writes is no answer at all, so a hostile cookie cannot decide it. */
export function parseConsent(raw: unknown): Consent | null {
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const identityLink = (parsed as { identityLink?: unknown }).identityLink;
    return typeof identityLink === 'boolean' ? { identityLink } : null;
  } catch {
    return null;
  }
}

export function readConsent(shared: StorageAdapter, namespace: string): Consent | null {
  return parseConsent(shared.getItem(keyFor(namespace, CONSENT_SUFFIX)));
}

/** The answer rides the SHARED store beside the anonymous id, so every
 *  subdomain of the site and the login's server read the same one. */
export function writeConsent(shared: StorageAdapter, namespace: string, consent: Consent): void {
  const key = keyFor(namespace, CONSENT_SUFFIX);
  if (typeof consent.identityLink !== 'boolean') {
    shared.removeItem(key);
    return;
  }
  shared.setItem(key, JSON.stringify({ identityLink: consent.identityLink }));
}

export function adoptAnonymousId(storage: StorageAdapter, shared: StorageAdapter, namespace: string, anonymousId: string): string | undefined {
  const key = keyFor(namespace, 'anonymous_id');
  const previous = valid(shared.getItem(key)) ?? valid(storage.getItem(key));
  shared.setItem(key, anonymousId);
  storage.setItem(key, anonymousId);
  return previous !== undefined && previous !== anonymousId ? previous : undefined;
}

export class IdentityManager {
  private anonymousId!: string;
  private distinctId!: string;
  private identified!: boolean;

  constructor(
    private readonly storage: StorageAdapter,
    private readonly shared: StorageAdapter,
    private readonly ids: IdFactory,
    private readonly namespace: string,
  ) {
    this.refresh();
  }

  refresh(): void {
    const fromShared = valid(this.shared.getItem(this.key('anonymous_id')));
    const fromLocal = valid(this.storage.getItem(this.key('anonymous_id')));
    if (fromShared) {
      this.anonymousId = fromShared;
      if (fromLocal !== fromShared) {
        this.storage.setItem(this.key('anonymous_id'), fromShared);
      }
    } else if (fromLocal) {
      this.anonymousId = fromLocal;
      this.shared.setItem(this.key('anonymous_id'), fromLocal);
    } else {
      this.anonymousId = this.ids.uuid();
      this.shared.setItem(this.key('anonymous_id'), this.anonymousId);
      this.storage.setItem(this.key('anonymous_id'), this.anonymousId);
    }
    const storedDistinct = valid(this.storage.getItem(this.key('distinct_id')));
    if (storedDistinct) {
      this.distinctId = storedDistinct;
      this.identified = true;
    } else {
      this.distinctId = this.anonymousId;
      this.identified = false;
    }
  }

  getAnonymousId(): string {
    return this.anonymousId;
  }

  getDistinctId(): string {
    return this.distinctId;
  }

  isIdentified(): boolean {
    return this.identified;
  }

  snapshot(): IdentitySnapshot {
    return {
      anonymousId: this.anonymousId,
      distinctId: this.distinctId,
      identified: this.identified,
    };
  }

  consent(): Consent | null {
    return readConsent(this.shared, this.namespace);
  }

  setConsent(consent: Consent): void {
    writeConsent(this.shared, this.namespace, consent);
  }

  identify(distinctId: string): IdentifyResult {
    const changed = !this.identified || this.distinctId !== distinctId;
    const linked = this.storage.getItem(this.key('linked_anonymous_id'));
    const owed = changed || linked !== this.anonymousId;
    this.distinctId = distinctId;
    this.identified = true;
    if (changed) {
      this.storage.setItem(this.key('distinct_id'), distinctId);
    }
    // A declined visitor leaves no marker either, so the link is still owed the
    // day they allow it.
    if (!owed || this.consent()?.identityLink === false) {
      return { changed };
    }
    this.storage.setItem(this.key('linked_anonymous_id'), this.anonymousId);
    return { anonymousToLink: this.anonymousId, changed };
  }

  reset(): void {
    this.anonymousId = this.ids.uuid();
    this.distinctId = this.anonymousId;
    this.identified = false;
    this.shared.setItem(this.key('anonymous_id'), this.anonymousId);
    this.storage.setItem(this.key('anonymous_id'), this.anonymousId);
    this.storage.removeItem(this.key('distinct_id'));
    this.storage.removeItem(this.key('linked_anonymous_id'));
  }

  private key(suffix: string): string {
    return keyFor(this.namespace, suffix);
  }
}
